<!-- TelcoTrack - Geometrie import modal (GeoJSON / DXF) -->
<!-- Centered dialog for file / import / result (and in-modal mapping when not map-hosted). -->
<app-modal
  [isOpen]="isDialogOpen"
  [title]="getModalTitle()"
  [showFooter]="false"
  modalClass="modal--import-surface"
  [width]="showInlineMapPreview ? 'min(96vw, 72rem)' : ''"
  [maxWidth]="showInlineMapPreview ? '96vw' : '90vw'"
  (closeEvent)="close()">

  <!-- Stepper (P1 #13): visual progress File → Cíl a mapování → Import → Výsledek. -->
  <app-stepper
    *ngIf="mode === 'import'"
    [steps]="importWizardSteps"
    [activeId]="currentStep"
    ariaLabel="Průběh importu geometrie"
  ></app-stepper>

  <!-- =========================================================================
       IMPORT MODE: Step 1 - File Selection
       ========================================================================= -->
  <div *ngIf="mode === 'import' && currentStep === 'file'" class="space-y-4">
    <div>
      <label for="file-input" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
        Vyberte soubor geometrie
      </label>
      <input
        id="file-input"
        type="file"
        accept=".geojson,.json,.dxf,application/json,application/geo+json,image/vnd.dxf,application/dxf"
        (change)="onFileSelected($event)"
        class="block w-full text-sm text-neutral-500 dark:text-neutral-400
               file:mr-4 file:py-2 file:px-4
               file:rounded-md file:border-0
               file:text-sm file:font-semibold
               file:bg-primary-50 file:text-primary-700
               hover:file:bg-primary-100
               dark:file:bg-primary-900 dark:file:text-primary-300">
      <p class="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
        Podporováno: GeoJSON (.geojson / .json) a DXF (.dxf). DXF (ASCII) se převede na serveru. DGN zatím není podporováno — exportujte do DXF.
        Po analýze se otevře mapa — přiřadíte typ objektu (R100, MET, UR, BUDOVA, …).
      </p>
      <p *ngIf="selectedFile" class="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
        Vybrán: {{ selectedFile.name }} ({{ (selectedFile.size / 1024).toFixed(2) }} KB)
      </p>
      <p *ngIf="fileError" class="mt-2 text-sm text-red-600">{{ fileError }}</p>
    </div>

    <div class="flex justify-end space-x-2">
      <app-button
        variant="secondary"
        (onClick)="close()">
        Zrušit
      </app-button>
      <app-button
        variant="primary"
        [loading]="isAnalyzing"
        [disabled]="!selectedFile || isAnalyzing"
        (onClick)="analyzeFile()">
        Další
      </app-button>
    </div>
  </div>

  <!-- =========================================================================
       IMPORT MODE: Step 2 - Layer Mapping (in-dialog when not map-hosted)
       ========================================================================= -->
  <ng-container *ngIf="mode === 'import' && currentStep === 'mapping' && !mapHostedMapping">
    <ng-container *ngTemplateOutlet="importMappingStepTpl"></ng-container>
  </ng-container>

  <!-- =========================================================================
       IMPORT MODE: Step 3 - Importing
       ========================================================================= -->
  <div *ngIf="mode === 'import' && currentStep === 'importing'" class="space-y-4 text-center py-8">
    <app-spinner size="xl" class="mx-auto"></app-spinner>
    <p class="text-lg font-medium text-neutral-900 dark:text-neutral-100">
      {{ importProgress.phase === 'originals' ? 'Ukládám původní geometrie pro přeřazení...' : (importProgress.phase === 'finishing' ? 'Dokončuji (hranice, adresy)...' : 'Importuji objekty (skupování, přidávání bodů)...') }}
    </p>
    <p *ngIf="importProgress.total > 0" class="text-sm font-medium text-primary-600 dark:text-primary-400">
      {{ importProgress.imported }} / {{ importProgress.total }}
    </p>
    <p class="text-sm text-neutral-600 dark:text-neutral-400">Prosím čekejte</p>
  </div>

  <!-- =========================================================================
       IMPORT MODE: Step 4 - Result
       ========================================================================= -->
  <div *ngIf="mode === 'import' && currentStep === 'result'" class="space-y-4">
    <div *ngIf="importError" class="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-sm">
      {{ importError }}
    </div>

    <div *ngIf="importResult && !importError">
      <div class="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 mb-4">
        <h3 class="text-lg font-semibold text-green-900 mb-2">Import dokončen</h3>
        <p class="text-sm text-green-700">{{ importResult.message }}</p>
      </div>

      <div class="space-y-2 text-sm">
        <div class="flex justify-between">
          <span class="text-neutral-600 dark:text-neutral-400">Prvků ve souboru:</span>
          <span class="font-medium">{{ importResult.statistics.total }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-neutral-600 dark:text-neutral-400">Vytvořeno objektů:</span>
          <span class="font-medium text-green-600">{{ importResult.statistics.imported }}</span>
        </div>
        <div *ngIf="(importResult.statistics.total - importResult.statistics.imported - importResult.statistics.failed) > 0 && importResult.statistics.failed === 0" class="flex justify-between text-neutral-500 dark:text-neutral-400">
          <span>Seskupeno do více-segmentových objektů:</span>
          <span>{{ importResult.statistics.total - importResult.statistics.imported - importResult.statistics.failed }} prvků</span>
        </div>
        <div class="flex justify-between">
          <span class="text-neutral-600 dark:text-neutral-400">Selhalo:</span>
          <span class="font-medium" [class.text-red-600]="importResult.statistics.failed > 0">{{ importResult.statistics.failed }}</span>
        </div>
      </div>

      <!-- Seznam selhaných prvků s důvodem (pro diagnostiku) -->
      <div *ngIf="importResult.statistics.failed > 0 && importResult.details?.failed?.length" class="mt-4">
        <details class="rounded-xl bg-red-50/80 dark:bg-red-950/30">
          <summary class="px-3 py-2 cursor-pointer text-sm font-medium text-red-800 dark:text-red-200">
            Selhané prvky ({{ importResult.details?.failed?.length ?? 0 }})
          </summary>
          <ul class="px-3 pb-3 pt-1 text-sm text-red-700 dark:text-red-300 max-h-60 overflow-y-auto space-y-1">
            <li *ngFor="let f of (importResult.details?.failed ?? [])" class="flex flex-col gap-0.5">
              <span class="font-medium">{{ f.name || 'Unnamed' }}</span>
              <span class="text-xs opacity-90">{{ f.error }}</span>
            </li>
          </ul>
        </details>
      </div>
    </div>

    <div class="flex justify-end mt-6">
      <app-button
        variant="primary"
        (onClick)="finish()">
        Hotovo
      </app-button>
    </div>
  </div>

  <!-- =========================================================================
       EXPLORE MODE: Layer List
       ========================================================================= -->
  <div *ngIf="mode === 'explore' && currentStep === 'explore'" class="space-y-4">
    <!-- Error message -->
    <div *ngIf="exploreError" class="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-sm">
      {{ exploreError }}
    </div>

    <!-- Operation result message -->
    <div *ngIf="operationResult" 
         class="p-3 rounded text-sm"
         [ngClass]="operationResult.success ? 'rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-green-700 dark:text-emerald-300' : 'rounded-xl bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300'">
      {{ operationResult.message }}
    </div>

    <!-- Loading state -->
    <div *ngIf="isLoadingExploreLayers" class="text-center py-8">
      <app-spinner size="lg" class="mx-auto"></app-spinner>
      <p class="mt-2 text-sm text-neutral-600 dark:text-neutral-400">Načítání vrstev...</p>
    </div>

    <!-- Empty state -->
    <div *ngIf="!isLoadingExploreLayers && filteredExploreLayers.length === 0" class="text-center py-8">
      <p class="text-neutral-600 dark:text-neutral-400">Žádné vrstvy nebyly nalezeny pro tento polygon.</p>
    </div>

    <!-- File filter (when multiple import files exist and the modal is not scoped to one file) -->
    <div *ngIf="!isLoadingExploreLayers && exploreLayers.length > 0 && exploreFilesList.length > 0 && !scopedImportFileName"
         class="flex items-center gap-2 flex-wrap">
      <label for="explore-file-filter" class="text-sm font-medium text-neutral-700 dark:text-neutral-300">Soubor:</label>
      <select
        id="explore-file-filter"
        [ngModel]="selectedExploreFile"
        (ngModelChange)="selectedExploreFile = $event; onExploreFileFilterChange()"
        class="tt-form-control min-w-[200px]">
        <option [ngValue]="null">Všechny soubory</option>
        <option *ngFor="let file of exploreFilesList" [ngValue]="file">
          {{ file || '(bez souboru)' }}
        </option>
      </select>
      <span *ngIf="selectedExploreFile !== null" class="text-sm text-neutral-500 dark:text-neutral-400">
        ({{ filteredExploreLayers.length }} {{ filteredExploreLayers.length === 1 ? 'vrstva' : 'vrstev' }})
      </span>
    </div>

    <!-- Layers table -->
    <div *ngIf="!isLoadingExploreLayers && filteredExploreLayers.length > 0" class="space-y-2">
      <!-- Headers -->
      <div class="grid grid-cols-12 gap-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300 pb-1 px-3">
        <span class="col-span-3">Vrstva</span>
        <span class="col-span-2">Typ</span>
        <span class="col-span-1 text-center">Počet</span>
        <span class="col-span-1 text-center">Rozprac.</span>
        <span class="col-span-5">Změnit na</span>
      </div>

      <!-- Layers List -->
      <div class="space-y-2">
        <div
          *ngFor="let layer of filteredExploreLayers"
          class="import-modal-layer-row grid grid-cols-12 gap-2 items-center p-3 tt-surface transition-colors"
          [ngClass]="!layer.skipped && layer.modifiedCount > 0
            ? 'import-modal-layer-row--modified'
            : ''">
          
          <!-- Layer Name (and source file when same name from different imports) -->
          <span class="col-span-3 text-sm truncate flex flex-col" [title]="layer.fileName ? (layer.layerName + ' – ' + layer.fileName) : layer.layerName"
                [ngClass]="!layer.skipped && layer.modifiedCount > 0
                  ? 'text-orange-700 dark:text-orange-400'
                  : 'text-neutral-700 dark:text-neutral-300'">
            <span>{{ layer.layerName || 'UNNAMED_LAYER' }}</span>
            <span *ngIf="layer.fileName" class="text-xs text-neutral-500 dark:text-neutral-400 truncate">{{ layer.fileName }}</span>
          </span>
          
          <!-- Current Object Type -->
          <span class="col-span-2 text-sm truncate" [title]="getObjectTypeLabel(layer.objectType)"
                [ngClass]="!layer.skipped && layer.modifiedCount > 0
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-neutral-600 dark:text-neutral-400'">
            {{ getObjectTypeLabel(layer.objectType) }}
          </span>
          
          <!-- Total Count -->
          <span class="col-span-1 text-center text-sm"
                [ngClass]="!layer.skipped && layer.modifiedCount > 0
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-neutral-600 dark:text-neutral-400'">
            {{ layer.totalCount }}
          </span>
          
          <!-- Modified Count -->
          <span class="col-span-1 text-center text-sm" 
                [ngClass]="!layer.skipped && layer.modifiedCount > 0 ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-neutral-500'">
            {{ layer.modifiedCount }}
          </span>
          
          <!-- New Type Dropdown -->
          <div class="col-span-5">
            <select
              [(ngModel)]="layer.newObjectType"
              [compareWith]="compareTypes"
              class="tt-form-control">
              <option *ngFor="let objType of objectTypes" [ngValue]="objType.value">
                {{ objType.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-2 pt-4">
      <app-button
        variant="primary"
        (onClick)="onSaveAllChanges()"
        [disabled]="!hasUnsavedChanges() || isProcessingOperation">
        Uložit změny
      </app-button>
      <app-button
        variant="secondary"
        (onClick)="close()">
        Zavřít
      </app-button>
    </div>
  </div>

  <!-- =========================================================================
       REASSIGNING: Loading animation (same style as import)
       ========================================================================= -->
  <div *ngIf="currentStep === 'reassigning'" class="space-y-4 text-center py-8">
    <app-spinner size="xl" class="mx-auto"></app-spinner>
    <p class="text-lg font-medium text-neutral-900 dark:text-neutral-100">Přeřazuji vrstvy...</p>
    <p class="text-sm text-neutral-600 dark:text-neutral-400">Skupování linií, přidávání bodů (1 m), vytváření objektů. Prosím čekejte.</p>
  </div>

  <!-- =========================================================================
       WARNING DIALOG: Modified Objects
       ========================================================================= -->
  <div *ngIf="currentStep === 'warning'" class="space-y-4">
    <!-- Loading state -->
    <div *ngIf="isCheckingModified" class="text-center py-8">
      <app-spinner size="lg" class="mx-auto"></app-spinner>
      <p class="mt-2 text-sm text-neutral-600 dark:text-neutral-400">Kontroluji objekty...</p>
    </div>

    <!-- Warning content -->
    <div *ngIf="!isCheckingModified && modifiedObjectsCheck" class="space-y-4">
      <div class="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30">
        <h3 class="text-lg font-semibold text-yellow-900 mb-2 flex items-center">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
          </svg>
          Varování: Rozpracované objekty
        </h3>
        <p class="text-sm text-yellow-800 mb-3">
          Vrstva "{{ modifiedObjectsCheck.layerName }}" obsahuje {{ modifiedObjectsCheck.modifiedCount }} rozpracovaných objektů 
          z celkových {{ modifiedObjectsCheck.totalCount }}.
        </p>
      </div>

      <!-- Modified objects list -->
      <div *ngIf="modifiedObjectsCheck.modifiedObjects.length > 0" class="max-h-48 overflow-y-auto rounded">
        <table appResizableTable class="w-full text-sm">
          <thead class="bg-neutral-100 dark:bg-neutral-700 sticky top-0">
            <tr>
              <th class="px-3 py-2 text-left text-neutral-700 dark:text-neutral-300">ID</th>
              <th class="px-3 py-2 text-left text-neutral-700 dark:text-neutral-300">Status</th>
              <th class="px-3 py-2 text-center text-neutral-700 dark:text-neutral-300">Fotek</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let obj of modifiedObjectsCheck.modifiedObjects">
              <td class="px-3 py-2 text-neutral-700 dark:text-neutral-300 truncate max-w-[150px]" [title]="obj.name">
                {{ obj.name }}
              </td>
              <td class="px-3 py-2 text-neutral-600 dark:text-neutral-400">
                {{ obj.statusName || obj.status }}
              </td>
              <td class="px-3 py-2 text-center text-neutral-600 dark:text-neutral-400">
                {{ obj.photoCount }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="text-sm text-neutral-700 dark:text-neutral-300 font-medium">
        Jste si jistí, že to chcete provést? Bude to vést ke ztracení procesu objektů!
      </p>

      <!-- Processing state -->
      <div *ngIf="isProcessingOperation" class="text-center py-4">
        <app-spinner size="lg" class="mx-auto"></app-spinner>
        <p class="mt-2 text-sm text-neutral-600 dark:text-neutral-400">Provádím operaci...</p>
      </div>

      <!-- Actions -->
      <div *ngIf="!isProcessingOperation" class="flex justify-between pt-4">
        <app-button
          variant="secondary"
          (onClick)="onWarningCancel()">
          Zrušit
        </app-button>
        <app-button
          variant="primary"
          (onClick)="onWarningConfirm()"
          title="Provede akci na všech objektech a resetuje jejich status na počáteční">
          Potvrdit (reset statusu)
        </app-button>
      </div>
    </div>
  </div>
</app-modal>

  <!-- Shared mapping step (dialog OR map side panel) -->
  <ng-template #importMappingStepTpl>
    <div class="space-y-4 import-mapping-step">
    <div *ngIf="analysisError" class="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-sm">
      {{ analysisError }}
    </div>

    <div *ngIf="!analysisError" class="space-y-4">
      <div *ngIf="detectedSourceCrs" class="tt-surface px-3 py-2 text-sm">
        <p class="text-sm text-neutral-700 dark:text-neutral-300">
          Detekováno:
          <span class="font-medium">{{ detectedSourceCrs }}</span>
        </p>
      </div>

      <div class="tt-surface p-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div *ngIf="showPolygonPicker">
            <span class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Polygon <span class="text-red-500">*</span>
            </span>
            <app-filter-multi-select
              label="Polygon"
              [options]="polygonFilterOptions"
              [selected]="polygonFilterSelected"
              [singleSelect]="true"
              emptyLabel="Vyberte polygon"
              controlClass="w-full"
              [disabled]="isLoadingPolygons"
              (selectedChange)="onPolygonFilterChange($event)"
            ></app-filter-multi-select>
          </div>
          <div *ngIf="isNewPolygon" class="col-span-2">
            <label for="new-polygon-name" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Název stavby <span class="text-red-500">*</span>
            </label>
            <input
              id="new-polygon-name"
              type="text"
              [(ngModel)]="newPolygonName"
              placeholder="Zadejte název stavby"
              required
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-kia-code" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              KIA kód
            </label>
            <input
              id="new-polygon-kia-code"
              type="text"
              [(ngModel)]="newPolygonKiaCode"
              placeholder="Volitelné"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-realization-year" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Rok realizace <span class="text-red-500">*</span>
            </label>
            <input
              id="new-polygon-realization-year"
              type="text"
              [(ngModel)]="newPolygonRealizationYear"
              placeholder="např. 2026"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-folder-number" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Číslo složky
            </label>
            <input
              id="new-polygon-folder-number"
              type="text"
              [(ngModel)]="newPolygonFolderNumber"
              placeholder="Volitelné"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-construction-type" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Způsob stavby
            </label>
            <select
              id="new-polygon-construction-type"
              [(ngModel)]="newPolygonConstructionType"
              class="tt-form-control">
              <option value="">Nevyplněno</option>
              <option *ngFor="let type of constructionTypeOptions" [value]="type">{{ type }}</option>
            </select>
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-expected-start" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Očekávaný start výstavby
            </label>
            <input
              id="new-polygon-expected-start"
              type="text"
              inputmode="numeric"
              placeholder="dd.mm.yyyy"
              [(ngModel)]="newPolygonExpectedConstructionStart"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-planned-end" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Plánovaný konec výstavby
            </label>
            <input
              id="new-polygon-planned-end"
              type="text"
              inputmode="numeric"
              placeholder="dd.mm.yyyy"
              [(ngModel)]="newPolygonPlannedConstructionEnd"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon" class="col-span-2">
            <p class="text-sm font-semibold text-neutral-800 dark:text-neutral-200 pt-1">Identifikace stavby</p>
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-sap-construction" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              SAP stavby
            </label>
            <input
              id="new-polygon-sap-construction"
              type="text"
              [(ngModel)]="newPolygonSapConstruction"
              placeholder="Volitelné"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-responsible-branch" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Zodpovědná pobočka <span class="text-red-500">*</span>
            </label>
            <select
              id="new-polygon-responsible-branch"
              [(ngModel)]="newPolygonResponsibleBranch"
              class="tt-form-control">
              <option value="">Nevybráno</option>
              <option *ngFor="let branch of responsibleBranchOptions" [value]="branch">{{ branch }}</option>
            </select>
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-project-state" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Stav složky
            </label>
            <select
              id="new-polygon-project-state"
              [(ngModel)]="newPolygonProjectState"
              class="tt-form-control">
              <option value="">Nevyplněno</option>
              <option *ngFor="let state of projectStateOptions" [value]="state">{{ state }}</option>
            </select>
          </div>
          <div *ngIf="isNewPolygon" class="col-span-2">
            <label for="new-polygon-description" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Popis
            </label>
            <textarea
              id="new-polygon-description"
              rows="2"
              [(ngModel)]="newPolygonDescription"
              placeholder="Volitelné"
              class="tt-form-control"></textarea>
          </div>
          <div *ngIf="isNewPolygon" class="col-span-2">
            <p class="text-sm font-semibold text-neutral-800 dark:text-neutral-200 pt-1">Stavební deník</p>
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-stavebnik" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Stavebník / investor <span class="text-red-500">*</span>
            </label>
            <input
              id="new-polygon-stavebnik"
              type="text"
              [(ngModel)]="newPolygonStavebnik"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-projektant" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Projektant
            </label>
            <input
              id="new-polygon-projektant"
              type="text"
              [(ngModel)]="newPolygonProjektant"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-zhotovitel" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Zhotovitel
            </label>
            <input
              id="new-polygon-zhotovitel"
              type="text"
              [(ngModel)]="newPolygonZhotovitel"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-stavbyvedouci" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Vedoucí stavby
            </label>
            <input
              id="new-polygon-stavbyvedouci"
              type="text"
              maxlength="150"
              [(ngModel)]="newPolygonStavbyveduciName"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-stavebni-povoleni" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Číslo stavebního povolení
            </label>
            <input
              id="new-polygon-stavebni-povoleni"
              type="text"
              [(ngModel)]="newPolygonStavebniPovoleniCislo"
              class="tt-form-control">
          </div>
          <div *ngIf="isNewPolygon">
            <label for="new-polygon-obec" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Obec
            </label>
            <input
              id="new-polygon-obec"
              type="text"
              [(ngModel)]="newPolygonObec"
              class="tt-form-control">
          </div>
        </div>

        <!-- Layer Mapping + optional inline preview (map-hosted uses live /maps) -->
        <div
          class="tt-surface px-3 py-2 text-sm space-y-1"
          *ngIf="mapHostedMapping">
          <p class="text-sm font-medium text-neutral-800 dark:text-neutral-100">
            Vrstvy na mapě
          </p>
          <p class="text-xs text-neutral-500 dark:text-neutral-400">
            Geometrie jsou vykreslené na mapě pod tímto panelem. Klikněte na vrstvu nebo na geometrii a přiřaďte typ objektu.
          </p>
          <p *ngIf="hostOverlayHint" class="text-xs text-neutral-500 dark:text-neutral-400">
            {{ hostOverlayHint }}
          </p>
        </div>

        <div
          class="import-modal-mapping-layout"
          [class.import-modal-mapping-layout--list-only]="mapHostedMapping">
          <div *ngIf="showInlineMapPreview" class="import-modal-mapping-layout__map space-y-2">
            <p class="text-sm font-semibold text-neutral-700 dark:text-neutral-300">
              Náhled vrstev na mapě
            </p>
            <p class="text-xs text-neutral-500 dark:text-neutral-400">
              Každá barva = CAD vrstva. Klikněte na vrstvu v seznamu nebo na geometrii na mapě a přiřaďte typ objektu.
            </p>
            <app-import-layer-map-preview
              [geoJson]="mapPreviewGeoJson"
              [layers]="layers"
              [selectedSourceCrs]="selectedSourceCrs"
              [detectedSourceCrs]="detectedSourceCrs"
              [highlightLayerName]="previewHighlightLayerName"
              [fitLayerName]="previewFitLayerName"
              (layerActivated)="onMapLayerActivated($event)"
              (fitLayerHandled)="onPreviewFitHandled()"
            ></app-import-layer-map-preview>
          </div>

          <div class="import-modal-mapping-layout__list space-y-4 min-w-0">
            <!-- Headers + master visibility toggle -->
            <div class="flex items-start gap-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300 pb-1">
              <label
                class="w-8 shrink-0 flex items-center justify-center"
                [attr.title]="allLayersVisibleOnMap ? 'Skrýt vše' : 'Zobrazit vše'">
                <input
                  type="checkbox"
                  [ngModel]="allLayersVisibleOnMap"
                  (ngModelChange)="toggleAllLayersVisibility()"
                  class="rounded text-primary-600 focus:ring-primary-500 border-0 bg-background-tertiary"
                  aria-label="Všechny vrstvy" />
              </label>
              <span class="import-modal-layer-swatch shrink-0 invisible" aria-hidden="true"></span>
              <span class="flex-1 min-w-0 flex items-center justify-between gap-2 min-h-[1.5rem]">
                <span>Původní vrstva (CAD)</span>
                <button
                  type="button"
                  class="text-xs font-medium text-primary-700 dark:text-primary-300 hover:underline shrink-0"
                  (click)="toggleAllLayersVisibility()"
                  [attr.title]="allLayersVisibleOnMap ? 'Skrýt vše' : 'Zobrazit vše'">
                  {{ allLayersVisibleOnMap ? 'Skrýt vše' : 'Zobrazit vše' }}
                </button>
              </span>
            </div>

            <!-- Layers List: line 1 = full CAD name, line 2 = type assignment.
                 No inner scroll — side panel body (or dialog) scrolls as one. -->
            <div class="space-y-2">
              <div
                *ngFor="let layer of layers; let i = index"
                class="import-modal-layer-row flex items-start gap-2 p-3 tt-surface transition-colors cursor-pointer"
                [class.import-modal-layer-row--active]="previewHighlightLayerName === layer.layerName"
                (mouseenter)="onPreviewLayerHover(layer.layerName)"
                (mouseleave)="onPreviewLayerHover(null)"
                (click)="onPreviewLayerSelect(layer.layerName)">

                <label
                  class="w-8 shrink-0 flex items-center justify-center pt-0.5"
                  (click)="$event.stopPropagation()"
                  [attr.title]="layer.visibleOnMap !== false ? 'Skrýt na mapě' : 'Zobrazit na mapě'">
                  <input
                    type="checkbox"
                    [(ngModel)]="layer.visibleOnMap"
                    (ngModelChange)="onLayerVisibilityChange()"
                    class="rounded text-primary-600 focus:ring-primary-500 border-0 bg-background-tertiary"
                    [attr.aria-label]="'Zobrazit vrstvu ' + originalCadLayerName(layer) + ' na mapě'" />
                </label>

                <span
                  class="import-modal-layer-swatch shrink-0 mt-1.5"
                  [style.background-color]="layerPreviewColor(i)"
                  aria-hidden="true"
                ></span>

                <div class="import-modal-layer-row__body flex-1 min-w-0">
                  <span
                    class="import-modal-layer-row__name block text-sm font-medium text-neutral-800 dark:text-neutral-100"
                    [title]="originalCadLayerName(layer)">
                    {{ originalCadLayerName(layer) }}
                  </span>
                  <div class="import-modal-layer-row__assign mt-1.5" (click)="$event.stopPropagation()">
                    <label class="sr-only" [attr.for]="'import-layer-type-' + i">
                      Přiřadit typ pro {{ originalCadLayerName(layer) }}
                    </label>
                    <select
                      [id]="'import-layer-type-' + i"
                      [(ngModel)]="layer.mappedType"
                      class="tt-form-control w-full"
                      [disabled]="layer.featureCount === 0 || isLoadingObjectTypes">
                      <option *ngFor="let objType of objectTypes" [value]="objType.value">
                        {{ objType.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>

    <!-- Validation messages -->
    <div *ngIf="!canProceedToImport()" class="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-900 dark:text-amber-200">
      <p class="mb-1">• Polygon je povinný</p>
      <p *ngIf="isNewPolygon" class="mb-1">• U nového polygonu je název stavby povinný</p>
    </div>

    <div class="flex justify-between pt-4">
      <app-button
        variant="secondary"
        size="sm"
        [iconOnly]="true"
        ariaLabel="Zpět"
        (onClick)="goBackToFileStep()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
        </svg>
      </app-button>
      <app-button
        variant="primary"
        [loading]="isImporting"
        [disabled]="!canProceedToImport() || isImporting"
        (onClick)="importGeoJson()">
        Importovat
      </app-button>
    </div>
    </div>
  </ng-template>


<!-- Map-hosted mapping: side panel over live Leaflet map (no modal chrome). -->
<aside
  *ngIf="isMapHostedMappingActive"
  class="import-mapping-side-panel"
  role="dialog"
  aria-modal="false"
  aria-label="Cíl a mapování importu geometrie">
  <header class="import-mapping-side-panel__header">
    <div class="min-w-0">
      <h2 class="import-mapping-side-panel__title">{{ getModalTitle() }}</h2>
      <p class="import-mapping-side-panel__subtitle">Cíl a mapování vrstev</p>
    </div>
    <button
      type="button"
      class="btn-icon shrink-0 text-neutral-500 hover:text-neutral-800 dark:text-neutral-300 dark:hover:text-neutral-100"
      aria-label="Zavřít mapování"
      (click)="close()">
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    </button>
  </header>
  <app-stepper
    [steps]="importWizardSteps"
    [activeId]="currentStep"
    ariaLabel="Průběh importu geometrie"
  ></app-stepper>
  <div class="import-mapping-side-panel__body">
    <ng-container *ngTemplateOutlet="importMappingStepTpl"></ng-container>
  </div>
</aside>
